import { Dropdown, type MenuProps } from 'antd';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useFileUpload } from '../../../hooks/useFileUpload';
import { useAppStore } from '../../../store/store';
import ShortcutHint from '../../Common/ShortcutHint';
import { Icon } from '../../Core/Icon';

interface FilesContextMenuProps {
  position: { x: number; y: number; direction: 'up' | 'down' };
  visible: boolean;
  isUploadDisabledInCurrentFolder: boolean;
  onClose: () => void;
}

export const FilesContextMenu: React.FC<FilesContextMenuProps> = ({
  position,
  visible,
  isUploadDisabledInCurrentFolder,
  onClose,
}) => {
  const { t } = useTranslation();
  const { uploadFiles } = useFileUpload();
  const triggerCreateFolder = useAppStore(
    (state) => state.appEvents.fileCreation.triggerCreateFolder,
  );
  const triggerCreateFileFromTemplate = useAppStore(
    (state) => state.appEvents.fileCreation.triggerCreateFileFromTemplate,
  );
  const triggerCreateSecretFile = useAppStore(
    (state) => state.appEvents.fileCreation.triggerCreateSecretFile,
  );
  const triggerCreateNoteFile = useAppStore(
    (state) => state.appEvents.fileCreation.triggerCreateNoteFile,
  );

  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  /**
   * Handles the click event for menu items.
   */
  const handleMenuClick: MenuProps['onClick'] = (info) => {
    // Submenu parent — keep menu open so presets stay reachable.
    if (info.key === 'create-secret') {
      return;
    }
    switch (info.key) {
      case 'new-folder':
        triggerCreateFolder();
        break;
      case 'upload-file':
        fileInputRef.current?.click();
        break;
      case 'upload-folder':
        folderInputRef.current?.click();
        break;
      case 'create-word':
        triggerCreateFileFromTemplate('word');
        break;
      case 'create-excel':
        triggerCreateFileFromTemplate('excel');
        break;
      case 'create-powerpoint':
        triggerCreateFileFromTemplate('powerpoint');
        break;
      case 'create-secret-credentials':
        triggerCreateSecretFile('credentials');
        break;
      case 'create-secret-card':
        triggerCreateSecretFile('card');
        break;
      case 'create-secret-identity':
        triggerCreateSecretFile('identity');
        break;
      case 'create-secret-ssh':
        triggerCreateSecretFile('ssh_key');
        break;
      case 'create-secret-custom':
        triggerCreateSecretFile('custom');
        break;
      case 'create-note':
        triggerCreateNoteFile();
        break;
    }
    onClose();
  };

  /**
   * Handles file input change for file uploads.
   */
  const handleInputFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    uploadFiles(files as FileList);
    event.target.value = '';
  };

  /**
   * Handles folder input change for folder uploads.
   */
  const handleInputFolderChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    uploadFiles(files as FileList);
    event.target.value = '';
  };

  /**
   * Menu items for the context menu.
   * Since the component is not rendered when upload is disabled, we can always include all items.
   */
  const menuItems: MenuProps['items'] = useMemo(
    () => [
      {
        label: t('common.createFolder'),
        icon: <Icon name="FolderIcon" className="text-yellow-dark h-5 w-5" />,
        key: 'new-folder',
      },
      {
        type: 'divider',
      },
      {
        label: t('common.uploadFile'),
        icon: <Icon name="FileUpIcon" className="h-5 w-5" />,
        key: 'upload-file',
      },
      {
        label: t('common.uploadFolder'),
        icon: <Icon name="FolderUpIcon" className="h-5 w-5" />,
        key: 'upload-folder',
      },
      {
        type: 'divider',
      },
      {
        label: t('common.wordDocument'),
        icon: <Icon name="WordFileIcon" className="h-5 w-5" />,
        key: 'create-word',
      },
      {
        label: t('common.excelDocument'),
        icon: <Icon name="ExcelFileIcon" className="h-5 w-5" />,
        key: 'create-excel',
      },
      {
        label: t('common.pptDocument'),
        icon: <Icon name="PptFileIcon" className="h-5 w-5" />,
        key: 'create-powerpoint',
      },
      {
        type: 'divider',
      },
      {
        label: t('note.createNote'),
        icon: <Icon name="FilePenLineIcon" className="text-azure h-5 w-5" />,
        key: 'create-note',
      },
      {
        label: t('secret.createSecret'),
        icon: <Icon name="FileKeyIcon" className="text-yellow-dark h-5 w-5" />,
        key: 'create-secret',
        children: [
          {
            extra: <ShortcutHint shortcutId="create.secret" />,
            label: t('secret.presets.credentials'),
            icon: <Icon name="KeyRoundIcon" className="text-yellow-dark h-5 w-5" />,
            key: 'create-secret-credentials',
          },
          {
            extra: <ShortcutHint shortcutId="create.secretCard" />,
            label: t('secret.presets.card'),
            icon: <Icon name="CreditCardIcon" className="text-yellow-dark h-5 w-5" />,
            key: 'create-secret-card',
          },
          {
            extra: <ShortcutHint shortcutId="create.secretIdentity" />,
            label: t('secret.presets.identity'),
            icon: <Icon name="FileUserIcon" className="text-yellow-dark h-5 w-5" />,
            key: 'create-secret-identity',
          },
          {
            extra: <ShortcutHint shortcutId="create.secretSshKey" />,
            label: t('secret.presets.ssh_key'),
            icon: <Icon name="KeyIcon" className="text-yellow-dark h-5 w-5" />,
            key: 'create-secret-ssh',
          },
          {
            extra: <ShortcutHint shortcutId="create.secretCustom" />,
            label: t('secret.presets.custom'),
            icon: <Icon name="FileSlidersIcon" className="text-yellow-dark h-5 w-5" />,
            key: 'create-secret-custom',
          },
        ],
      },
    ],
    [t],
  );

  const menuWidth = 180;
  let left = position.x;
  let top = position.y;

  if (left + menuWidth > window.innerWidth) {
    left = window.innerWidth - menuWidth - 10;
  }

  // Don't render anything if upload is disabled in current folder
  if (isUploadDisabledInCurrentFolder) {
    return null;
  }

  // Don't render anything if there are no menu items to show
  if (menuItems.length === 0) {
    return null;
  }

  return (
    <>
      <input
        type="file"
        multiple
        ref={fileInputRef}
        className="hidden"
        onChange={handleInputFileChange}
      />
      <input
        type="file"
        multiple
        webkitdirectory="true"
        mozdirectory="true"
        ref={folderInputRef}
        className="hidden"
        onChange={handleInputFolderChange}
      />
      <Dropdown
        autoAdjustOverflow
        getPopupContainer={() => document.body}
        menu={{
          items: menuItems,
          onClick: handleMenuClick,
        }}
        classNames={{
          root: 'files-context-menu',
        }}
        trigger={['contextMenu']}
        open={visible}
        styles={{
          root: {
            position: 'fixed',
            top,
            left,
            pointerEvents: 'auto',
            maxHeight: 'max-content',
            overflow: 'visible',
          },
        }}
        transitionName={position.direction === 'up' ? 'slide-up-custom' : 'slide-down-custom'}
        onOpenChange={(open) => {
          if (!open) {
            onClose();
          }
        }}
      />
    </>
  );
};

export default FilesContextMenu;
